<template>
	<div id="wrapper">
		<SidebarView :reports="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="container">
					<div class="card card-shape home-box">
						<div class="card-header">
							<div class="row align-items-center">
								<div class="col-6">
									<h6 class="m-0 fw-bold text-green">
										Balance Sheet Summary (Vertical)
									</h6>
								</div>
								<div class="col-6 text-end">
									<b-button @click="generateExcel"
										class="btn-sm me-2 akkurate-green-btn akkurate-small">
										<font-awesome-icon :icon="['fas', 'file-excel']" />
									</b-button>
									<report-filter :form="form" @filterAction="filterAction"
										:filter="filter"
										:ytd-terms-uses-financial-year-dates="false"></report-filter>
									<b-button @click="printPDF" class="btn-sm me-2 akkurate-green-btn akkurate-small">
										<font-awesome-icon :icon="['fas', 'print']" />
									</b-button>
									<b-button @click="downloadPDF" class="btn-sm akkurate-green-btn akkurate-small">
										<font-awesome-icon :icon="['fas', 'download']" />
									</b-button>
								</div>
							</div>
						</div>
						<div class="card-body" ref="pdfContent">
							<div class="row">
								<div class="col-12 col-md-12">
									<statement-institution :institution="institution" :current_date="false"
										:title="'BALANCE SHEET SUMMARY (VERTICAL)'"></statement-institution>
								</div>
								<hr />
								<div class="row mb-2">
									<div class="col-12 small">
										Branch(es) : {{ branch_list_display }}
									</div>
								</div>
							</div>

							<div class="row">
								<div class="col-12 text-end">
									<div>
										Terms:
										<span class="fw-bold">{{
											form.terms
										}}</span>
									</div>
									<span class="mt-2">From:
										<strong>{{ form.start_date }}</strong>
										To: <strong>{{ form.end_date }}</strong>
									</span>
								</div>
							</div>
							<div v-if="isBusy" class="text-center text-danger my-2">
								<b-spinner class="align-middle"></b-spinner>
								<strong>Loading...</strong>
							</div>
							<div v-else id="balance-sheet" class="row">
								<div class="col-12 col-md-12 trans_details">
									<div class="row align-items-start">
										<!-- ASSETS (left) -->
										<div class="col-6 col-md-6 pe-0 trans_details">
											<b-table-simple responsive outlined>
												<b-thead>
													<b-tr>
														<b-th class="text-center fs-6 akkurate-green" colspan="4">
															<span>ASSETS</span>
														</b-th>
													</b-tr>
													<b-tr>
														<b-th>GL Code</b-th>
														<b-th>GL Name</b-th>
														<b-th></b-th>
														<b-th class="text-end">Amount</b-th>
													</b-tr>
												</b-thead>

												<b-tbody>
													<b-tr v-for="(asset, assetIndex) in assets_items"
														:key="'assets-' + assetIndex" class="list-item">
														<b-td class="py-0 px-2">{{ asset.code }}</b-td>
														<b-td class="py-0 px-2">{{ asset.display_name }}</b-td>
														<b-td></b-td>
														<b-td class="py-0 px-2 text-end" style="font-weight: bold">
															{{ institution.currency_code }}
															{{ displayNumber(Number(asset.total ?? 0).toFixed(2)) }}
														</b-td>
													</b-tr>
												</b-tbody>
											</b-table-simple>
										</div>

										<!-- LIABILITIES + CAPITAL (right) -->
										<div class="col-6 col-md-6 ps-0 trans_details">
											<b-table-simple responsive outlined>
												<b-thead>
													<b-tr>
														<b-th class="text-center fs-6 akkurate-green" colspan="4">
															<span>LIABILITIES + CAPITAL</span>
														</b-th>
													</b-tr>
													<b-tr>
														<b-th>GL Code</b-th>
														<b-th>GL Name</b-th>
														<b-th></b-th>
														<b-th class="text-end">Amount</b-th>
													</b-tr>
												</b-thead>

												<b-tbody>
													<b-tr v-for="(liability, liabilityIndex) in liability_items"
														:key="'liability-' + liabilityIndex" class="list-item">
														<b-td class="py-0 px-2">{{ liability.code }}</b-td>
														<b-td class="py-0 px-2">{{ liability.display_name }}</b-td>
														<b-td></b-td>
														<b-td class="py-0 px-2 text-end" style="font-weight: bold">
															{{ institution.currency_code }}
															{{ displayNumber(Number(liability.total ??
																liability.transactions[0]?.balance ?? 0).toFixed(2)) }}
														</b-td>
													</b-tr>

													<!-- surpluses -->
													<b-tr v-for="(value, item) in surpluses" :key="item"
														class="list-item">
														<b-td class="py-0 px-2">-</b-td>
														<b-td class="py-0 px-2" style="font-weight: bold">{{
															value.display_name }}</b-td>
														<b-td></b-td>
														<b-td class="py-0 px-2 text-end" style="font-weight: bold">
															{{ institution.currency_code }} {{
																displayNumber(value.transactions[0]?.balance ?? 0) }}
														</b-td>
													</b-tr>
												</b-tbody>


											</b-table-simple>
										</div>

										<div class="col-6 col-md-6 pe-0 trans_details">
											<b-table-simple outlined>
												<b-tbody>
													<b-tr>
														<b-th variant="secondary">Gross Asset</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(Number(grant_total_assets).toFixed(2)) }}
														</b-th>
													</b-tr>

													<b-tr>
														<b-th variant="secondary">PROVISION FOR LOAN LOSSES</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(Number(loan_losses).toFixed(2)) }}
														</b-th>
													</b-tr>

													<b-tr>
														<b-th variant="secondary">ACCUMULATED DEPRECIATION</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(accumulated_dep) }}
														</b-th>
													</b-tr>

													<b-tr>
														<b-th variant="secondary">Net Asset</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{ displayNumber(net_assets)
															}}
														</b-th>
													</b-tr>
												</b-tbody>
											</b-table-simple>
										</div>

										<div class="col-6 col-md-6 ps-0 trans_details  mt-auto">
											<b-table-simple outlined>
												<b-tbody>
													<b-tr>
														<b-th variant="secondary">Total Liability</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(Number(grant_total_liabilities).toFixed(2)) }}
														</b-th>
													</b-tr>

													<b-tr>
														<b-th variant="secondary">Total Surplus</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(Number(grant_total_surpluses).toFixed(2)) }}
														</b-th>
													</b-tr>

													<b-tr>
														<b-th variant="secondary">Total Liabilities + Surplus</b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary"></b-th>
														<b-th variant="secondary" class="text-end">
															{{ institution.currency_code }} {{
																displayNumber(Number(liabilities_plus_surplus).toFixed(2))
															}}
														</b-th>
													</b-tr>
												</b-tbody>
											</b-table-simple>
										</div>

										<div class="col-12">
											<b-table-simple responsive outlined>
												<b-thead>
													<b-tr>
														<b-th class="text-center fs-6 akkurate-green" colspan="6">
															<span>Information on Ratio</span>
														</b-th>
													</b-tr>
												</b-thead>
												<b-tbody>
													<b-tr class="trans_details">
														<b-th>Liquidity Reserve</b-th>
														<b-th>{{ liquidity_res }} %</b-th>
														<b-th colspan="2">(Total liquid Funds / total assets *
															100)</b-th>
													</b-tr>
													<b-tr class="trans_details">
														<b-th>Return on Asset</b-th>
														<b-th>{{ return_on_assets }} %</b-th>
														<b-th colspan="2">(Surplus difference / total assets *
															100)</b-th>
													</b-tr>
													<b-tr class="trans_details">
														<b-th>Capital Adequacy</b-th>
														<b-th>{{ capital_adequacy }} %</b-th>
														<b-th colspan="2">([Member Shares + total capital res] / total
															assets * 100)</b-th>
													</b-tr>
													<b-tr class="trans_details">
														<b-th>Prepared By</b-th>
														<b-th></b-th>
														<b-th>Certified By</b-th>
														<b-th></b-th>
													</b-tr>
													<b-tr class="trans_details">
														<b-th>Accountant</b-th>
														<b-th></b-th>
														<b-th>Manager/Board Treasurer</b-th>
														<b-th></b-th>
													</b-tr>
													<b-tr class="trans_details">
														<b-th>Signature</b-th>
														<b-th></b-th>
														<b-th>Signature</b-th>
														<b-th></b-th>
													</b-tr>
												</b-tbody>
											</b-table-simple>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>

		<!-- AI -->
		 <ChatBot />
		<!-- End AI -->
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>
	</div>
</template>
<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import Footer from "@/views/main/components/Footer.vue";
import StatementInstitution from "@/views/main/components/institution/StatementInstitution.vue";
import ApiService from "@/core/services/api.service";
import {
	postBalanceSheetReport,
	unwrapBalanceSheetPayload,
} from "@/services/balanceSheetReport.service";
import { showFinancialReportApiError } from "@/utils/financialReportApiErrors";
import Multiselect from "vue-multiselect";
import html2pdf from "html2pdf.js";
import ReportFilter from "../form/ReportFilter.vue";
import ExcelJS from "exceljs";
import ChatBot from "@/views/components/AI/ChatBot.vue";
export default {
	components: {
		SidebarView,
		TopbarView,
		Footer,
		StatementInstitution,
		Multiselect,
		ReportFilter,
		ChatBot,
	},
	data() {
		return {
			form: {
				start_date: this.getBeginningOfYear(),
				end_date: new Date().toISOString().split("T")[0],
				branches: [],
				products: [],
				branch_options: [],
				product_options: [],
				users: [],
				users_options: [],
				status: [],
				coa: [],
				coa_options: [],
				terms: "YTD",
				terms_options: ["YTD", "Periodic"],
				categories: [],
				loan_status:[],
			},
			filter: {
				branches: true,
				products: false,
				users: false,
				status: false,
				condition: false,
				terms: true,
				coa: false,
				voucher_status: false,
				start_date: true,
				end_date: true,
				loan_status:false,
			},
			search: "Search",
			grant_total_liabilities: 0,
			liabilities_plus_surplus: 0,
			grant_total_surpluses: 0,
			grant_total_assets: 0,
			isBusy: false,
			isSearching: false,
			selected_num: "10",
			numbers_options: [10, 50, 100, 500],
			isLoadingInstitution: false,
			institution: "",
			start_date: "",
			end_date: "",
			net_assets: 0,
			fields: [
				{
					key: "account_number",
					label: "Account Number",
				},
				{
					key: "customer_name",
					label: "Customer Name",
				},
				{
					key: "amount",
					label: "Amount",
				},
				{
					key: "min_balance",
					label: "Minimum Balance",
				},
				{
					key: "total",
					label: "Total",
				},
			],
			perPage: 20,
			currentPage: 1,
			items: [],
			provisions: [],
			assets_items: [],
			liability_items: [],
			surpluses: [],
		};
	},
	computed: {
		rows() {
			return this.items.length;
		},
		sumLoanBalance() {
			let total = 0;
			for (let i = 0; i < this.items.length; i++) {
				total = this.items[i].loan_balance;
			}
			return total;
		},
		sumSavingBalance() {
			let total = 0;
			for (let i = 0; i < this.items.length; i++) {
				total = this.items[i].saving_balance;
			}

			return total;
		},
		liquidity_res() {
			return (
				Number(
					(this.liquid_funds / this.grant_total_assets) * 100
				).toFixed(2) ?? 0
			);
		},
		return_on_assets() {
			return (
				Number(
					(this.grant_total_surpluses / this.grant_total_assets) *
					100
				).toFixed(2) ?? 0
			);
		},
		capital_adequacy() {
			return (
				Number(
					((this.member_shares + this.reserves) /
						this.grant_total_assets) *
					100
				).toFixed(2) ?? 0
			);
		},
		branch_list_display() {
			const selected = this.form.branches || [];
			const all = this.form.branch_options || [];

			if (selected.length > 0) {
				return selected.map(b => b.name).join(", ");
			}
			if (all.length > 0) {
				return all.map(b => b?.name).join(", ");
			}
			return "";
		}
	},

	created() {
		this.getInstitution();
		this.getBalanceSheets();
		this.getProductDropdown();
	},
	methods: {
		async getBalanceSheets() {
			this.items = [];
			this.isBusy = true;
			this.search = "Processing...";
			this.isSearching = true;
			await postBalanceSheetReport({
				summary: true,
				layout: "vertical",
				form: this.form,
			})
				.then((response) => {
					this.isBusy = false;
					this.search = "Search";
					this.isSearching = false;
					var res = unwrapBalanceSheetPayload(response);
					this.assets_items = res.assets;
					this.liability_items = res.liabilities;
					this.surpluses = res.surpluses;
					this.grant_total_assets = res.grant_total_assets;
					this.grant_total_liabilities = res.grant_total_liabilities;
					this.grant_total_surpluses = res.total_surpluses;
					this.liabilities_plus_surplus = this.grant_total_liabilities + this.grant_total_surpluses;
					this.start_date = this.form.start_date;
					this.end_date = this.form.end_date;
					this.provisions = res.provisions;
					this.loan_to_member = res.loan_to_member;
					this.loan_losses = res.loan_losses;
					this.accumulated_dep = res.accumulated_dep;
					this.liquid_funds = res.liquid_funds;
					this.reserves = res.reserves;
					this.member_shares = res.member_shares;
					this.net_assets = res.net_assets;
				})
				.catch((error) => {
					this.isBusy = false;
					this.search = "Search";
					this.isSearching = false;
					showFinancialReportApiError(this, error);
				});
		},
		async getProductDropdown() {
			this.isBusy = true;
			this.search = "Processing...";
			await ApiService.query("/reports/controls/products/dropdown")
				.then((response) => {
					// console.log(response.data);
					this.isBusy = false;
					this.search = "Search";
					this.form.branch_options = response.data.branches;
					this.form.product_options = response.data.products;
					this.form.coa_options = response.data.coa_subheads;
				})
				.catch((error) => {
					// console.log(error);
					this.isBusy = false;
					this.search = "Search";
				});
		},
		downloadPDF() {
			const content = this.$refs.pdfContent; // Reference to the HTML content you want to convert
			// console.log("here");
			const pdfOptions = {
				margin: [0.5, 0.2, 0.8, 0.2], //top, left, buttom, right
				filename: "statement.pdf",
				image: { type: "jpeg", quality: 0.98 },
				html2canvas: { scale: 2 },
				pagebreak: { mode: "slice" },
				jsPDF: {
					unit: "in",
					format: "letter",
					orientation: "portrait",
				},
			};
			html2pdf()
				.from(content)
				.set(pdfOptions)
				.toPdf()
				.get("pdf")
				.then((pdf) => {
					var totalPages = pdf.internal.getNumberOfPages();
					var insit_name = this.institution.name;
					var insit_tele = this.institution.telephone;
					var insit_email = this.institution.email;
					var insit_motto = this.institution.motto;

					for (var i = 1; i <= totalPages; i++) {
						pdf.setPage(i);
						pdf.setFontSize(8);
						pdf.setTextColor(150);
						pdf.text(
							[
								"For Inquiries, Please Contact " +
								insit_name +
								", Customer Service on " +
								insit_tele +
								"",
								"or email us on " +
								insit_email +
								" " +
								insit_motto +
								"",
								"Page " + i + " of " + totalPages,
							],
							pdf.internal.pageSize.getWidth() / 2,
							pdf.internal.pageSize.getHeight() - 0.6,
							{ align: "center" }
						);
					}
				})
				.save();
		},
		printPDF() {
			const content = this.$refs.pdfContent; // Reference to the HTML content you want to convert
			console.log(content);
			// console.log("here");
			const pdfOptions = {
				margin: [0.5, 0.2, 0.8, 0.2], //top, left, buttom, right
				filename: "statement.pdf",
				image: { type: "jpeg", quality: 0.98 },
				html2canvas: { scale: 2 },
				pagebreak: { mode: "slice" },
				jsPDF: {
					unit: "in",
					format: "letter",
					orientation: "portrait",
				},
			};
			html2pdf()
				.from(content)
				.set(pdfOptions)
				.toPdf()
				.get("pdf")
				.then((pdf) => {
					var totalPages = pdf.internal.getNumberOfPages();
					var insit_name = this.institution.name;
					var insit_tele = this.institution.telephone;
					var insit_email = this.institution.email;
					var insit_motto = this.institution.motto;

					for (var i = 1; i <= totalPages; i++) {
						pdf.setPage(i);
						pdf.setFontSize(8);
						pdf.setTextColor(150);
						pdf.text(
							[
								"For Inquiries, Please Contact " +
								insit_name +
								", Customer Service on " +
								insit_tele +
								"",
								"or email us on " +
								insit_email +
								" " +
								insit_motto +
								"",
								"Page " + i + " of " + totalPages,
							],
							pdf.internal.pageSize.getWidth() / 2,
							pdf.internal.pageSize.getHeight() - 0.6,
							{ align: "center" }
						);
					}
					// Get the blob of the PDF
					return pdf.output("blob");
				})
				.then((pdfBlob) => {
					// Create a URL for the PDF blob
					var pdfUrl = URL.createObjectURL(pdfBlob);

					// Open the URL in a new tab
					const printWindow = window.open(
						pdfUrl,
						"_blank",
						"width=800,height=600"
					);
					printWindow.onload = () => {
						printWindow.print();
						URL.revokeObjectURL(pdfUrl);
					};
				})
				.catch((error) => {
					console.error("Error generating PDF:", error);
				});
		},
		filterTable() {
			this.getBalanceSheets();
		},
		// changePerPageNumber(data) {
		//   this.perPage = data;
		//   this.getReceiptListing();
		// },
		async getInstitution() {
			this.isLoadingInstitution = true;
			await ApiService.get("/settings/institution")
				.then((response) => {
					this.institution = response.data.data;
				})
				.catch((error) => {
					console.log(error);
				})
				.finally(() => {
					this.isLoadingInstitution = false;
				});
		},
		filterAction() {
			this.getBalanceSheets();
		},
		async generateExcel() {
			const fileUrl = "/REPORT_TEMPLATE_NEW.xlsx"; // Path to the file in the public folder
			const imageUrl = "/default_logo.png"; // Path to your image file

			// Institution details
			const institutionName = this.institution.name;
			const institutionMotto = this.institution.motto;
			const institutionAddress = this.institution.address;
			const institutionPhone = this.institution.telephone;
			const institutionEmail = this.institution.email;
			const date =
				"FROM: " +
				this.form.start_date +
				" TO: " +
				this.form.end_date;
			const terms = "TERMS:  " + this.form.terms;

			try {
				// Fetch the Excel file from the public URL
				const response = await fetch(fileUrl);
				if (!response.ok) {
					throw new Error(
						`Network response was not ok: ${response.statusText}`
					);
				}
				const arrayBuffer = await response.arrayBuffer();

				// Load the file into ExcelJS
				const wb = new ExcelJS.Workbook();
				await wb.xlsx.load(arrayBuffer);

				// Access the first sheet
				const ws = wb.getWorksheet(1); // Get the first worksheet

				// Add Image to the top of the sheet
				const imageResponse = await fetch(imageUrl);
				const imageBuffer = await imageResponse.arrayBuffer();
				const imageId = wb.addImage({
					buffer: imageBuffer,
					extension: "png", // Adjust this if you're using a different image type (e.g., jpg)
				});

				// Insert the image at the top-left corner (row 1, column 1)
				ws.addImage(imageId, {
					tl: { col: 0, row: 0 }, // Top-left corner (adjust this as needed)
					ext: { width: 300, height: 200 }, // Set the image size (adjust width/height as needed)
				});

				// Insert institution details next to the image (starting at row 1, col 5)
				const institutionDetailsStartCol = 5; // Column where text starts after image
				const institutionDetailsRow = 1; // Row for institution details

				// Add the institution name, address, and phone number
				ws.getCell(`F${institutionDetailsRow}`).value =
					institutionName;
				ws.getCell(`F${institutionDetailsRow}`).font = {
					bold: true,
					size: 14,
				}; // Bold and bigger font for name

				ws.getCell(`F${institutionDetailsRow + 1}`).value =
					institutionMotto;
				ws.getCell(`F${institutionDetailsRow + 1}`).font = {
					size: 12,
				}; // Regular font size for address

				ws.getCell(`F${institutionDetailsRow + 2}`).value =
					institutionAddress;
				ws.getCell(`F${institutionDetailsRow + 2}`).font = {
					size: 12,
				}; // Regular font size for phone

				ws.getCell(`F${institutionDetailsRow + 3}`).value =
					institutionPhone;
				ws.getCell(`F${institutionDetailsRow + 3}`).font = {
					size: 12,
				}; // Regular font size for phone

				ws.getCell(`F${institutionDetailsRow + 4}`).value =
					institutionEmail;
				ws.getCell(`F${institutionDetailsRow + 4}`).font = {
					size: 12,
				}; // Regular font size for phone

				ws.getCell(`F${institutionDetailsRow + 6}`).value = date;
				ws.getCell(`F${institutionDetailsRow + 6}`).font = {
					size: 12,
				}; // Regular font size for phone

				ws.getCell(`F${institutionDetailsRow + 7}`).value = terms;
				ws.getCell(`F${institutionDetailsRow + 7}`).font = {
					size: 12,
				}; // Regular font size for phone

				// Optionally adjust the column width if the text is long
				ws.getColumn("E").width = 100; // Adjust the width of column D (if necessary)

				// Define header row
				const head1 = ["ASSESTS"];
				const head2 = ["LIABILITIES + CAPITAL"];
				const head3 = ["Information on Ratio"];
				const header = ["GL Code", "GL Name", "Amount"];

				// Add header row starting from row 13 (or the next row after the image and details)
				const startingRow = institutionDetailsRow + 10; // Row 4 after institution details
				let headerRow = ws.getRow(startingRow);
				headerRow.values = head1;
				headerRow.font = {
					bold: true,
					underline: "singleAccounting",
				};
				headerRow.commit(); // Commit the row to the worksheet

				// Add individual data rows
				let currentRow = startingRow + 1;
				headerRow = ws.getRow(currentRow);
				headerRow.values = header;
				headerRow.font = { bold: true };
				headerRow.commit(); // Commit the row to the worksheet
				currentRow++;

				// Process each item
				this.assets_items.forEach((item) => {
					const row = ws.getRow(currentRow);
					// Map item values to the corresponding header columns
					row.values = [
						item.code,
						item.display_name,
						item.total, // Remove extra spaces
					];
					row.commit();
					currentRow++;
				});
				const row_1 = ws.getRow(currentRow);
				// Process each item
				row_1.values = [
					"Gross Asset",
					" ", // Remove extra spaces
					this.grant_total_assets,
				];
				row_1.commit();
				currentRow++;

				const row_2 = ws.getRow(currentRow);
				// Process each item
				row_2.values = [
					"PROVISION FOR LOAN LOSSES",
					" ", // Remove extra spaces
					this.loan_losses,
				];
				row_2.commit();
				currentRow++;

				const row_3 = ws.getRow(currentRow);
				// Process each item
				row_3.values = [
					"ACCUMULATED DEPRECIATION",
					" ", // Remove extra spaces
					this.accumulated_dep,
				];
				row_3.commit();
				currentRow++;

				const row_4 = ws.getRow(currentRow);
				// Process each item
				row_4.values = [
					"Net Asset",
					" ", // Remove extra spaces
					this.net_assets,
				];
				row_4.commit();
				currentRow += 2;

				headerRow = ws.getRow(currentRow);
				headerRow.values = head2;
				headerRow.font = { bold: true };
				headerRow.commit(); // Commit the row to the worksheet

				currentRow++;
				headerRow = ws.getRow(currentRow);
				headerRow.values = header;
				headerRow.font = { bold: true };
				headerRow.commit(); // Commit the row to the worksheet
				currentRow++;

				this.liability_items.forEach((item) => {
					const row = ws.getRow(currentRow);
					// Map item values to the corresponding header columns
					row.values = [
						item.code,
						item.display_name,
						item.transactions[0]?.balance, // Remove extra spaces
					];
					row.commit();
					currentRow++;
				});

				const row_5 = ws.getRow(currentRow);
				// Process each item
				row_5.values = [
					"Total Liability",
					" ", // Remove extra spaces
					this.grant_total_liabilities,
				];
				row_5.commit();
				currentRow++;

				const row_6 = ws.getRow(currentRow);
				// Process each item
				row_6.values = [
					"Total Surplus",
					" ", // Remove extra spaces
					this.grant_total_surpluses,
				];
				row_6.commit();
				currentRow++;

				const row_7 = ws.getRow(currentRow);
				// Process each item
				row_7.values = [
					"Total Liabilities + Surplus",
					" ", // Remove extra spaces
					this.liabilities_plus_surplus,
				];
				row_7.commit();
				currentRow += 2;

				headerRow = ws.getRow(currentRow);
				headerRow.values = head3;
				headerRow.font = {
					bold: true,
					underline: "singleAccounting",
				};
				headerRow.commit();

				currentRow++;
				const row_8 = ws.getRow(currentRow);
				// Process each item
				row_8.values = [
					"Liquidity Reserve",
					`${this.liquidity_res} %`, // Remove extra spaces
					"(Total liquid Funds / total assets * 100)",
				];
				row_8.commit();
				currentRow++;

				const row_9 = ws.getRow(currentRow);
				// Process each item
				row_9.values = [
					"Return on Asset",
					`${this.return_on_assets} %`, // Remove extra spaces
					"(Surplus difference / total assets * 100)",
				];
				row_9.commit();
				currentRow++;

				const row_10 = ws.getRow(currentRow);
				// Process each item
				row_10.values = [
					"Capital Adequacy",
					`${this.capital_adequacy} %`, // Remove extra spaces
					"([Member Shares + total capital res] / total assets * 100)",
				];
				row_10.commit();
				currentRow++;

				const row_11 = ws.getRow(currentRow);
				// Process each item
				row_11.values = [
					"Prepared By",
					" ", // Remove extra spaces
					"Certified By",
				];
				row_11.commit();
				currentRow++;

				const row_12 = ws.getRow(currentRow);
				// Process each item
				row_12.values = [
					"Accountant",
					" ", // Remove extra spaces
					"Manager/Board Treasurer",
				];
				row_12.commit();
				currentRow++;

				const row_13 = ws.getRow(currentRow);
				// Process each item
				row_13.values = [
					"Signature",
					" ", // Remove extra spaces
					"Signature",
				];
				row_13.commit();
				currentRow++;

				const buffer = await wb.xlsx.writeBuffer();
				const blob = new Blob([buffer], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});

				// Create a download link and simulate a click to download the file
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = `Balance_Sheet_Summary_${this.form.start_date}_to_${this.form.end_date}.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} catch (err) {
				console.error(
					"Error fetching, modifying, or downloading the Excel file:",
					err.message
				);
			}
		},
	},
};
</script>
